import {
  VIEW_LABELS,
  dateLong,
  entityRail,
  eur,
  internesFor,
  filterByTraitement,
  kindChips,
  listRows,
  modeCounts,
  people,
  planKpis,
  traitementCounts,
  typeCounts,
  viewCounts,
  type Affaire,
  type PlanFilters,
  type PlanView,
  type TraitementDto,
} from '@cockpit/shared';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  FilterChip,
  Input,
  KpiStrip,
  KpiTile,
  Notice,
  SegmentedControl,
  Select,
  SourceTag,
  Spinner,
  Table,
  Tabs,
  Td,
  Th,
  cn,
} from '@cockpit/ui';
import { ChevronDown, Info, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../components/Layout.js';
import { AffaireCard } from '../components/plan/AffaireCard.js';
import { AffaireDrawer } from '../components/plan/Detail.js';
import { RecTable } from '../components/plan/RecTable.js';
import { entLabel } from '../components/plan/bits.js';
import { usePlanFilters } from '../components/plan/usePlanFilters.js';
import { dateTime } from '../lib/format.js';
import { usePlan, useTraitement } from '../lib/queries.js';

const PAGE = 40;
const FMAX_PRESETS = [25, 50, 80];
const CAMIN_PRESETS = [20, 50, 100];

const VIEW_SORT: Record<PlanView, string> = {
  plan: 'triés par priorité, échéance puis enjeu',
  drift: 'triés par marge perdue',
  dates: 'triés par nombre d’alertes puis CA',
  sav: 'pertes nettes d’abord',
  rec: 'triés par écart non expliqué',
};

const VIEW_NOTE: Partial<Record<PlanView | 'ratt' | 'past' | 'all', string>> = {
  ratt: 'Rattrapables : il reste au moins 10 % des coûts à engager. Les DA peuvent encore agir sur les achats et les heures, même si la date de fin affichée est dépassée.',
  past: 'Constatées : les coûts sont presque tous engagés. Restent l’avenant, la réclamation et le retour d’expérience.',
  all: 'Toutes les dérives d’au moins 3 k€.',
  dates:
    'À transmettre aux DA pour corriger le planning dans le suivi d’affaire. La fin estimée projette la date de fin au rythme actuel des coûts engagés.',
  sav: 'Écart de chaque ligne de commande au chiffrage (LineDiscount) : négatif = gain, positif = perte. Commandes annulées exclues.',
  rec: 'Même convention que le saving : négatif = achats moins chers que prévu. Si le saving est bien tracé, l’écart achats revu − prévu est proche du saving déclaré (tolérance : 5 % des achats prévus, 1 000 € au minimum). Vérifiez aussi les achats engagés : un budget revu pas encore commandé n’a pas encore de saving.',
};

function NumberFilter({
  label,
  unit,
  value,
  presets,
  onChange,
}: {
  label: string;
  unit: string;
  value: number | null;
  presets: number[];
  onChange: (v: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  useEffect(() => setDraft(value === null ? '' : String(value)), [value]);
  useEffect(() => {
    const t = setTimeout(() => {
      const n = draft.trim() === '' || Number.isNaN(Number(draft)) ? null : Math.max(0, Number(draft));
      if (n !== value) onChange(n);
    }, 300);
    return () => clearTimeout(t);
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-2 whitespace-nowrap text-[13.5px] text-ink">
        {label}
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          value={draft}
          placeholder="—"
          onChange={(e) => setDraft(e.target.value)}
          className="tnum h-8 w-20 px-2"
        />
        <span className="text-ink-3">{unit}</span>
      </label>
      <span className="flex gap-1">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={value === p}
            onClick={() => onChange(value === p ? null : p)}
            className={cn(
              'tnum rounded-pill border px-2 py-0.5 text-[12px] font-semibold transition-colors duration-fast',
              value === p
                ? 'border-action bg-action text-on-action'
                : 'border-line text-ink-3 hover:text-ink',
            )}
          >
            {p} {unit}
          </button>
        ))}
      </span>
    </div>
  );
}

export function PlanPage() {
  const q = usePlan();
  const [f, set] = usePlanFilters();
  const toggle = useTraitement();
  const [limit, setLimit] = useState(PAGE);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [search, setSearch] = useState(f.q);
  useEffect(() => setLimit(PAGE), [f]);
  useEffect(() => {
    const t = setTimeout(() => search !== f.q && set({ q: search }), 250);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const info = useMemo(
    () => new Map<string, TraitementDto>((q.data?.traitements ?? []).map((t) => [t.code, t])),
    [q.data],
  );
  const done = useMemo(() => new Set(info.keys()), [info]);

  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorState>{(q.error as Error).message}</ErrorState>;
  const d = q.data!.dataset;
  const filt: PlanFilters = d.ents[f.ent] || f.ent === 'Tous' ? f : { ...f, ent: 'Tous' };

  const rail = entityRail(d, filt);
  const types = typeCounts(d, filt);
  const counts = viewCounts(d, filt);
  const kpis = planKpis(d, filt);
  const chips = kindChips(d, filt);
  const modes = modeCounts(d, filt);
  const who = people(d, filt);
  const listed = listRows(d, filt, done);
  const trtCounts = traitementCounts(listed, done);
  const rows = filterByTraitement(listed, done, filt.trt);
  const internes = internesFor(d, filt);
  const showEnt = filt.ent === 'Tous';
  const all = [...d.rows, ...d.savx];
  const opened = openCode ? (all.find((r) => r.code === openCode) ?? null) : null;
  const onToggle = (code: string) => toggle.mutate({ code, done: !done.has(code) });
  const numericActive = filt.fmax !== null || filt.camin !== null;
  const note = filt.view === 'drift' ? VIEW_NOTE[filt.dmode] : VIEW_NOTE[filt.view];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Plan d’action chantiers"
        title={filt.ent === 'Tous' ? 'Toutes les entités' : entLabel(filt.ent)}
        sub="Ce que chaque directeur d’agence doit traiter, pourquoi les marges baissent, et les dates à corriger."
        aside={
          <span className="flex flex-col items-end gap-1 text-[12px] text-ink-3">
            <SourceTag source="OPE" date={dateLong(d.asOf)} />
            <span>
              {q.data!.source === 'sync' ? 'Synchronisé depuis Flex' : `Source : ${q.data!.source}`} ·{' '}
              {dateTime(q.data!.syncedAt)}
            </span>
          </span>
        }
      />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Entité">
          {rail.map((e) => (
            <FilterChip
              key={e.code}
              active={filt.ent === e.code}
              onClick={() => set({ ent: e.code, who: '' })}
            >
              {e.code === 'Tous' ? 'Toutes les entités' : entLabel(e.code)}
              {e.crit > 0 && (
                <span
                  title={`${e.crit} chantier${e.crit > 1 ? 's' : ''} critique${e.crit > 1 ? 's' : ''}`}
                  className={cn(
                    'tnum rounded-pill px-1.5 text-[11px] font-bold leading-[18px]',
                    filt.ent === e.code ? 'bg-on-action/20 text-on-action' : 'bg-danger-50 text-danger-700',
                  )}
                >
                  {e.crit}
                </span>
              )}
            </FilterChip>
          ))}
        </div>
        <Card padded={false} className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3">
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-ink-3">Type</span>
            <SegmentedControl
              label="Type d’affaire"
              size="sm"
              value={filt.ty || 'all'}
              onChange={(k) => set({ ty: k === 'all' ? '' : k })}
              options={[
                { key: 'all', label: 'Tous', count: types.all },
                ...types.types.map((t) => ({
                  key: t.code,
                  label: t.code === '-' ? 'Non renseigné' : t.code,
                  count: t.n,
                })),
              ]}
            />
          </div>
          <NumberFilter
            label="Facturé ≤"
            unit="%"
            value={filt.fmax}
            presets={FMAX_PRESETS}
            onChange={(v) => set({ fmax: v })}
          />
          <NumberFilter
            label="CA ≥"
            unit="k€"
            value={filt.camin}
            presets={CAMIN_PRESETS}
            onChange={(v) => set({ camin: v })}
          />
          {numericActive && (
            <Button
              size="sm"
              variant="ghost"
              icon={<X size={16} strokeWidth={1.75} />}
              onClick={() => set({ fmax: null, camin: null })}
            >
              Effacer
            </Button>
          )}
        </Card>
      </div>

      <Tabs
        label="Vue"
        value={filt.view}
        onChange={(v) => set({ view: v, kinds: [] })}
        tabs={(Object.keys(VIEW_LABELS) as PlanView[]).map((k) => ({
          key: k,
          label: VIEW_LABELS[k],
          count: counts[k],
        }))}
      />

      <KpiStrip cols={5}>
        {kpis.map((k) => (
          <KpiTile
            key={k.label}
            label={k.label}
            value={k.value}
            sub={k.hint}
            alert={k.alert && k.value !== '0'}
          />
        ))}
      </KpiStrip>

      <section aria-labelledby="liste" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="liste" className="font-display text-[18px] font-bold text-ink">
            {VIEW_LABELS[filt.view]}
            <span className="ml-2 font-body text-[14px] font-normal text-ink-3">
              {rows.length} affaire{rows.length > 1 ? 's' : ''} · {VIEW_SORT[filt.view]}
            </span>
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {filt.view === 'drift' && (
            <SegmentedControl
              label="Type de dérive"
              size="sm"
              value={filt.dmode}
              onChange={(k) => set({ dmode: k })}
              options={[
                { key: 'ratt', label: 'Rattrapables', count: modes.ratt },
                { key: 'past', label: 'Constatées', count: modes.past },
                { key: 'all', label: 'Toutes', count: modes.all },
              ]}
            />
          )}
          {filt.view === 'rec' && (
            <SegmentedControl
              label="Statut du rapprochement"
              size="sm"
              value={filt.rmode}
              onChange={(k) => set({ rmode: k })}
              options={[
                { key: 'drv', label: 'Écarts', count: modes.drv },
                { key: 'up', label: 'Surcoûts non tracés', count: modes.up },
                { key: 'dn', label: 'Baisses non tracées', count: modes.dn },
                { key: 'ok', label: 'Cohérents', count: modes.ok },
                { key: 'all', label: 'Toutes', count: modes.all },
              ]}
            />
          )}
          {filt.view === 'sav' && (
            <SegmentedControl
              label="Résultat achats"
              size="sm"
              value={filt.smode}
              onChange={(k) => set({ smode: k })}
              options={[
                { key: 'all', label: 'Toutes', count: modes.all },
                { key: 'perte', label: 'En perte nette', count: modes.perte },
                { key: 'gain', label: 'En gain net', count: modes.gain },
              ]}
            />
          )}
          <SegmentedControl
            label="Suivi du traitement"
            size="sm"
            value={filt.trt}
            onChange={(k) => set({ trt: k })}
            options={[
              { key: 'todo', label: 'À traiter', count: trtCounts.todo },
              { key: 'done', label: 'Traitées', count: trtCounts.done },
              { key: 'all', label: 'Toutes', count: trtCounts.all },
            ]}
          />
          {chips.map((c) => (
            <FilterChip
              key={c.key}
              count={c.n}
              active={filt.kinds.includes(c.key)}
              onClick={() =>
                set({
                  kinds: filt.kinds.includes(c.key)
                    ? filt.kinds.filter((k) => k !== c.key)
                    : [...filt.kinds, c.key],
                })
              }
            >
              {c.label}
            </FilterChip>
          ))}
          <span className="flex-1" />
          {filt.view === 'plan' && (
            <Select
              aria-label="Priorité"
              value={filt.prio}
              onChange={(e) => set({ prio: e.target.value as PlanFilters['prio'] })}
              className="h-9 w-auto"
            >
              <option value="ch">Critiques et hautes</option>
              <option value="crit">Critiques seulement</option>
              <option value="all">Toutes priorités</option>
            </Select>
          )}
          <Select
            aria-label="Responsable"
            value={filt.who}
            onChange={(e) => set({ who: e.target.value })}
            className="h-9 w-auto max-w-[220px]"
          >
            <option value="">Tous les responsables</option>
            {who.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </Select>
          <div className="relative">
            <Search
              size={16}
              strokeWidth={1.75}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3"
            />
            <Input
              type="search"
              aria-label="Rechercher une affaire"
              placeholder="Affaire, client ou code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-[220px] pl-8"
            />
          </div>
        </div>

        {note && (
          <Notice icon={<Info size={18} strokeWidth={1.75} />} className="py-2.5">
            {note}
          </Notice>
        )}

        {rows.length === 0 ? (
          <EmptyState title="Aucune affaire ne correspond à ces filtres">
            {filt.trt === 'todo' && trtCounts.done > 0
              ? `${trtCounts.done} affaire${trtCounts.done > 1 ? 's' : ''} traitée${trtCounts.done > 1 ? 's' : ''} masquée${trtCounts.done > 1 ? 's' : ''} : ouvrez l’onglet « Traitées » pour les rouvrir.`
              : 'Élargissez la sélection ou retirez un filtre.'}
          </EmptyState>
        ) : filt.view === 'rec' ? (
          <RecTable
            rows={rows.slice(0, limit) as Affaire[]}
            showEnt={showEnt}
            done={done}
            info={info}
            onToggle={onToggle}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {rows.slice(0, limit).map((r) => (
              <AffaireCard
                key={r.code}
                row={r}
                view={filt.view}
                showEnt={showEnt}
                done={done.has(r.code)}
                info={info.get(r.code)}
                onToggle={() => onToggle(r.code)}
                onOpen={() => setOpenCode(r.code)}
              />
            ))}
          </div>
        )}
        {rows.length > limit && (
          <Button variant="secondary" className="self-center" onClick={() => setLimit((l) => l + PAGE)}>
            Afficher {Math.min(PAGE, rows.length - limit)} de plus ({rows.length - limit} restantes)
          </Button>
        )}
      </section>

      <details className="group rounded-md border border-line-soft bg-surface shadow-od-1">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4">
          <span>
            <span className="font-display text-[15px] font-bold text-ink">
              Affaires internes et intra-groupe
            </span>
            <span className="tnum ml-2 text-[13.5px] text-ink-3">
              {internes.rows.length} affaire{internes.rows.length > 1 ? 's' : ''} · {eur(internes.cost)} de
              coûts pour {eur(internes.ca)} de CA
            </span>
          </span>
          <ChevronDown
            size={20}
            strokeWidth={1.75}
            className="shrink-0 text-ink-3 transition-transform duration-base group-open:rotate-180"
          />
        </summary>
        <div className="border-t border-line-soft px-3 pb-4 pt-3">
          <p className="mb-3 px-2 text-[13.5px] text-ink-2">
            Affaires ouvertes au nom de l’entité elle-même ou d’une société du groupe. Elles portent des coûts
            sans CA en face et sont exclues des listes ci-dessus. Action DA : identifier les coûts qui
            relèvent de vrais chantiers et les réaffecter.
          </p>
          {internes.rows.length ? (
            <Table minWidth={640}>
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th>Libellé</Th>
                  <Th>Nature</Th>
                  <Th align="right">CA prévu</Th>
                  <Th align="right">Coûts réalisés</Th>
                  <Th className="w-[22%]" />
                </tr>
              </thead>
              <tbody>
                {internes.rows.map((r) => {
                  const mx = Math.max(1, ...internes.rows.map((x) => x.cr));
                  return (
                    <tr key={r.code}>
                      <Td className="tnum text-ink-2">{r.code}</Td>
                      <Td>
                        {r.nom ?? r.cli}
                        {showEnt && <span className="text-ink-3"> · {entLabel(r.ent)}</span>}
                      </Td>
                      <Td>{r.kind}</Td>
                      <Td align="right">{eur(r.ca)}</Td>
                      <Td align="right" className="font-semibold">
                        {eur(r.cr)}
                      </Td>
                      <Td>
                        <span
                          className="block h-1.5 rounded-pill bg-danger-600/70"
                          style={{ width: `${Math.max(2, (r.cr / mx) * 100)}%` }}
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <p className="px-2 text-[13.5px] text-ink-3">Aucune affaire interne pour cette sélection.</p>
          )}
        </div>
      </details>

      <details className="rounded-md border border-line-soft bg-surface px-5 py-4 text-[13.5px] text-ink-2">
        <summary className="cursor-pointer font-display font-bold text-ink">Méthode de calcul</summary>
        <div className="mt-3 flex max-w-[90ch] flex-col gap-2">
          <p>
            Dérive de marge = marge révisée − marge prévue. Elle se décompose en achats (revu − prévu), MO et
            sous-traitance (non séparées dans le suivi d’affaire) et autres charges (frais, aléas,
            provisions). Une dérive est rattrapable s’il reste au moins 10 % des coûts revus à engager et que
            le chantier n’est pas entièrement facturé ou pas encore terminé. L’avancement des coûts sert de
            référence plutôt que les dates, souvent fausses.
          </p>
          <p>
            Priorité critique : enjeu d’au moins 50 k€ ou chantier à perte. Haute : au moins 15 k€. Solde à
            facturer et fin de chantier à partir de 5 k€ restant, sous-facturation dès 20 points d’écart entre
            coûts engagés et facturé (10 k€ minimum), dérive à partir de 5 k€.
          </p>
          <p>
            Les filtres « Facturé ≤ » et « CA ≥ » s’appliquent à toutes les vues et aux indicateurs, pas aux
            affaires internes. Les affaires présentes uniquement dans le saving n’ont pas de taux de
            facturation et restent affichées. L’état « traité » est partagé entre tous les utilisateurs.
          </p>
        </div>
      </details>

      <AffaireDrawer
        row={opened}
        onClose={() => setOpenCode(null)}
        done={opened ? done.has(opened.code) : false}
        info={opened ? info.get(opened.code) : undefined}
        onToggle={() => opened && onToggle(opened.code)}
      />
    </div>
  );
}
